import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import useDebounce from '../../hooks/useDebounce';

// Multiselect com checkboxes + "Selecionar todos" — mesmo padrão de portal/posicionamento
// do SearchableSelect, pra nunca ser cortado por overflow de modal/card.
export default function MultiSelect({ options, selected, onChange, placeholder = 'Buscar...' }) {
  const [aberto, setAberto] = useState(false);
  const [busca, setBusca] = useState('');
  const [posicao, setPosicao] = useState(null);
  const buscaDebounced = useDebounce(busca, 200);
  const triggerRef = useRef(null);
  const menuRef = useRef(null);

  const todosSelecionados = options.length > 0 && selected.length === options.length;
  const filtradas = options.filter((o) => o.label.toLowerCase().includes(buscaDebounced.toLowerCase()));

  useLayoutEffect(() => {
    if (!aberto || !triggerRef.current) return;
    const atualizar = () => {
      const rect = triggerRef.current.getBoundingClientRect();
      setPosicao({ top: rect.bottom + 6, left: rect.left, width: rect.width });
    };
    atualizar();
    window.addEventListener('resize', atualizar);
    return () => window.removeEventListener('resize', atualizar);
  }, [aberto]);

  useEffect(() => {
    if (!aberto) return;
    const fechar = (e) => {
      if (triggerRef.current?.contains(e.target) || menuRef.current?.contains(e.target)) return;
      setAberto(false);
    };
    const fecharAoRolar = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      setAberto(false);
    };
    document.addEventListener('mousedown', fechar);
    document.addEventListener('scroll', fecharAoRolar, true);
    return () => {
      document.removeEventListener('mousedown', fechar);
      document.removeEventListener('scroll', fecharAoRolar, true);
    };
  }, [aberto]);

  const toggleValor = (valor) => {
    if (selected.includes(valor)) {
      onChange(selected.filter((v) => v !== valor));
    } else {
      onChange([...selected, valor]);
    }
  };

  const toggleTodos = () => {
    onChange(todosSelecionados ? [] : options.map((o) => o.value));
  };

  let textoValor;
  if (options.length === 0) {
    textoValor = placeholder;
  } else if (todosSelecionados) {
    textoValor = 'Todos';
  } else if (selected.length === 0) {
    textoValor = 'Nenhum selecionado';
  } else if (selected.length === 1) {
    textoValor = options.find((o) => o.value === selected[0])?.label || '1 selecionado';
  } else {
    textoValor = `${selected.length} selecionados`;
  }

  return (
    <div className="am-select">
      <button
        ref={triggerRef}
        type="button"
        className="am-select__trigger"
        onClick={() => setAberto((a) => !a)}
      >
        <span className="am-select__value">{textoValor}</span>
        <span className="ms am-select__chevron">{aberto ? 'expand_less' : 'expand_more'}</span>
      </button>

      {aberto &&
        posicao &&
        createPortal(
          <div
            ref={menuRef}
            className="am-select__menu"
            style={{ top: posicao.top, left: posicao.left, width: Math.max(posicao.width, 260) }}
          >
            {options.length > 6 && (
              <input
                autoFocus
                type="text"
                className="am-select__search"
                placeholder={placeholder}
                value={busca}
                onChange={(e) => setBusca(e.target.value)}
              />
            )}
            <button type="button" className="am-select__opcao" onClick={toggleTodos} style={{ fontWeight: 700 }}>
              <span className="d-flex align-items-center gap-2">
                <span className={`am-checkbox__box${todosSelecionados ? ' is-checked' : ''}`}>
                  {todosSelecionados && <span className="ms">check</span>}
                </span>
                Todos
              </span>
            </button>
            <div className="am-select__opcoes">
              {filtradas.length === 0 && <div className="am-select__empty">Nenhum resultado</div>}
              {filtradas.map((o) => {
                const marcado = selected.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    className={`am-select__opcao${marcado ? ' is-selected' : ''}`}
                    onClick={() => toggleValor(o.value)}
                  >
                    <span className="d-flex align-items-center gap-2">
                      <span className={`am-checkbox__box${marcado ? ' is-checked' : ''}`}>
                        {marcado && <span className="ms">check</span>}
                      </span>
                      {o.label}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
